import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdArrowForward, MdLocationOn, MdSearch, MdWorkOutline } from 'react-icons/md';
import jobRoleService from '../services/jobRoleService';

const CareerPage = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('ALL');
  const [jobRoles, setJobRoles] = useState([]);
  const [groupedByCountry, setGroupedByCountry] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchJobRoles();
  }, []);

  const fetchJobRoles = async () => {
    try {
      setLoading(true);
      const response = await jobRoleService.getPublicJobRoles();
      setJobRoles(response.data.jobRoles);
      setGroupedByCountry(response.data.groupedByCountry);
    } catch (err) {
      console.error('Failed to fetch job roles:', err);
      setError('Failed to load job openings. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const countries = Object.keys(groupedByCountry);

  const visibleRoles = jobRoles.filter((role) => {
    const countryName = role.countryId?.name || 'Other';
    const matchesCountry = selectedCountry === 'ALL' || countryName === selectedCountry;
    const matchesSearch = `${role.title} ${countryName}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    return matchesCountry && matchesSearch;
  });

  const totalOpenings = visibleRoles.length;
  const totalCountries = countries.length;

  return (
    <div className="min-h-screen bg-[#F5F1E8] text-[#2E2E2E]">
      <div className="relative overflow-hidden border-b border-white/40 bg-[linear-gradient(135deg,rgba(107,127,92,0.12),rgba(245,241,232,0.92),rgba(193,122,92,0.12))]">
        <div className="absolute -left-20 top-10 h-64 w-64 rounded-full bg-[#6B7F5C]/15 blur-[90px]" />
        <div className="absolute right-0 top-0 h-72 w-72 rounded-full bg-[#C17A5C]/15 blur-[100px]" />

        <div className="relative mx-auto max-w-[1400px] px-6 py-14 sm:px-10 lg:px-24 lg:py-20">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="mb-8 inline-flex items-center gap-2 rounded-full border border-[#2E2E2E]/15 bg-white/70 px-4 py-2 text-sm font-semibold text-[#2E2E2E] backdrop-blur transition hover:-translate-y-0.5 hover:bg-white"
          >
            <span className="text-base">←</span>
            Back to Home
          </button>

          <div className="grid gap-10 lg:grid-cols-[1.2fr_0.8fr] lg:items-end">
            <div>
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-[#6B7F5C]/20 bg-white/70 px-4 py-2 text-xs font-semibold uppercase tracking-[0.22em] text-[#6B7F5C] backdrop-blur">
                <MdWorkOutline size={16} />
                Career Opportunities
              </div>
              <h1 className="max-w-[11ch] text-5xl font-black leading-[0.94] tracking-[-2px] sm:text-6xl lg:text-7xl">
                Explore Open Vacancies
              </h1>
              <p className="mt-6 max-w-2xl text-lg leading-8 text-[#5A5A5A]">
                Browse current overseas recruitment openings, filter by category, and apply once you find the right fit.
              </p>
            </div>

            <div className="grid grid-cols-3 gap-4 rounded-[28px] border border-white/60 bg-white/70 p-5 shadow-[0_16px_40px_rgba(46,46,46,0.08)] backdrop-blur-xl">
              <div>
                <div className="text-3xl font-black text-[#6B7F5C]">{totalOpenings}</div>
                <div className="mt-1 text-xs font-semibold uppercase tracking-[0.18em] text-[#7A7A7A]">Openings</div>
              </div>
              <div>
                <div className="text-3xl font-black text-[#C17A5C]">{totalCountries}</div>
                <div className="mt-1 text-xs font-semibold uppercase tracking-[0.18em] text-[#7A7A7A]">Countries</div>
              </div>
              <div>
                <div className="text-3xl font-black text-[#2E2E2E]">24/7</div>
                <div className="mt-1 text-xs font-semibold uppercase tracking-[0.18em] text-[#7A7A7A]">Support</div>
              </div>
            </div>
          </div>

          <div className="mt-10 grid gap-4 rounded-[28px] border border-white/60 bg-white/70 p-4 shadow-[0_16px_40px_rgba(46,46,46,0.08)] backdrop-blur-xl md:grid-cols-[1fr_auto] md:items-center">
            <label className="flex items-center gap-3 rounded-2xl border border-[#E0DDD5] bg-[#FDFCF9] px-4 py-3">
              <MdSearch size={20} className="text-[#7A7A7A]" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search role or category"
                className="w-full border-none bg-transparent text-sm outline-none placeholder:text-[#9A9A9A]"
              />
            </label>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setSelectedCountry('ALL')}
                className={`rounded-full px-4 py-3 text-sm font-semibold transition ${selectedCountry === 'ALL' ? 'bg-[#2E2E2E] text-white' : 'bg-[#FDFCF9] text-[#2E2E2E] border border-[#E0DDD5]'}`}
              >
                All Countries
              </button>
              {countries.slice(0, 6).map((countryName) => (
                <button
                  key={countryName}
                  type="button"
                  onClick={() => setSelectedCountry(countryName)}
                  className={`rounded-full px-4 py-3 text-sm font-semibold transition ${selectedCountry === countryName ? 'bg-[#6B7F5C] text-white' : 'bg-[#FDFCF9] text-[#2E2E2E] border border-[#E0DDD5]'}`}
                >
                  {countryName}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <main className="mx-auto max-w-[1400px] px-6 py-12 sm:px-10 lg:px-24 lg:py-16">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-[#6B7F5C] border-t-transparent"></div>
              <p className="mt-4 text-lg text-[#5A5A5A]">Loading job openings...</p>
            </div>
          </div>
        ) : error ? (
          <div className="rounded-[28px] border border-red-200 bg-red-50 p-10 text-center shadow-[0_12px_32px_rgba(46,46,46,0.06)]">
            <h2 className="text-2xl font-bold text-red-800">Error Loading Jobs</h2>
            <p className="mt-2 text-red-600">{error}</p>
            <button
              onClick={fetchJobRoles}
              className="mt-4 rounded-full bg-red-600 px-6 py-3 text-sm font-semibold text-white transition hover:bg-red-700"
            >
              Try Again
            </button>
          </div>
        ) : visibleRoles.length === 0 ? (
          <div className="rounded-[28px] border border-dashed border-[#D5CFC2] bg-white/70 p-10 text-center shadow-[0_12px_32px_rgba(46,46,46,0.06)]">
            <h2 className="text-2xl font-bold text-[#2E2E2E]">No vacancies found</h2>
            <p className="mt-2 text-[#5A5A5A]">Try a different search term or country.</p>
          </div>
        ) : (
          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {visibleRoles.map((role) => {
              const countryName = role.countryId?.name || 'International';
              const countryCode = role.countryId?.code || 'INT';

              return (
                <article
                  key={role._id}
                  className="group rounded-[28px] border border-white/70 bg-white/80 p-6 shadow-[0_16px_40px_rgba(46,46,46,0.08)] backdrop-blur-xl transition duration-300 hover:-translate-y-1 hover:shadow-[0_24px_50px_rgba(46,46,46,0.12)]"
                >
                  <div className="mb-5 flex items-start justify-between gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#6B7F5C]/10 text-[#6B7F5C]">
                      <MdWorkOutline size={24} />
                    </div>
                    <span className="rounded-full border border-[#C17A5C]/20 bg-[#FFF7F2] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.2em] text-[#A65E40]">
                      Open
                    </span>
                  </div>

                  <h3 className="text-xl font-bold leading-tight text-[#2E2E2E]">{role.title}</h3>
                  <p className="mt-3 line-clamp-3 text-sm leading-6 text-[#5A5A5A]">
                    {role.description || 'Join our overseas recruitment pipeline and move through application, interview, visa, and departure stages.'}
                  </p>

                  <div className="mt-5 flex items-center gap-2 text-sm text-[#7A7A7A]">
                    <MdLocationOn size={18} className="text-[#C17A5C]" />
                    {countryName}
                  </div>

                  <div className="mt-6 flex items-center justify-between gap-3">
                    <button
                      type="button"
                      onClick={() => navigate('/register')}
                      className="inline-flex items-center gap-2 rounded-full bg-[#2E2E2E] px-5 py-3 text-sm font-semibold text-white transition hover:-translate-y-0.5 hover:bg-[#1f1f1f]"
                    >
                      Apply Now
                      <MdArrowForward size={18} />
                    </button>
                    <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A8A8A]">
                      {countryCode}
                    </span>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default CareerPage;
